import React, { useState } from 'react';
import { useSales } from '../../context/SalesContext';
import {
  TrendingUp,
  ShoppingCart,
  Euro,
  Package,
  Calendar,
  BarChart3,
  RotateCcw,
  AlertCircle
} from 'lucide-react';
import './SalesDashboard.css';

const StatCard = ({ title, value, icon, color = 'primary', subtitle }) => (
  <div className="stat-card">
    <div className="stat-content">
      <div className="stat-info">
        <h3 className="stat-title">{title}</h3>
        <div className={`stat-value stat-${color}`}>{value}</div>
        {subtitle && (
          <div className="stat-subtitle">{subtitle}</div>
        )}
      </div>
      <div className={`stat-icon stat-${color}`}>
        {icon}
      </div>
    </div>
  </div>
);

const SalesDashboard = () => {
  const { sales, salesEnabled, stats, resetSales } = useSales();
  const [showResetDialog, setShowResetDialog] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleResetSales = () => {
    resetSales();
    setShowResetDialog(false);
  };

  if (!salesEnabled) {
    return (
      <div className="sales-dashboard">
        <div className="sales-alert">
          <AlertCircle size={20} />
          <div>
            <h3>Système de ventes en attente</h3>
            <p>Les ventes seront automatiquement activées lors du premier paiement effectué sur le site.</p>
          </div>
        </div>
        
        <div className="sales-card">
          <div className="sales-header">
            <BarChart3 size={24} />
            <h2>Tableau de Bord des Ventes</h2>
          </div>
          <p>Aucune vente enregistrée pour le moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sales-dashboard">
      <div className="sales-header-section">
        <div className="sales-title">
          <BarChart3 size={24} />
          <h1>Tableau de Bord des Ventes</h1>
          <div className="sales-status">
            <TrendingUp size={16} />
            <span>Actif</span>
          </div>
        </div>
        <button
          className="reset-btn"
          onClick={() => setShowResetDialog(true)}
        >
          <RotateCcw size={16} />
          Réinitialiser
        </button>
      </div>

      {/* Statistiques principales */}
      <div className="stats-grid">
        <StatCard
          title="Chiffre d'Affaires Total"
          value={formatCurrency(stats.totalRevenue)}
          icon={<Euro size={32} />}
          color="success"
        />
        <StatCard
          title="Nombre de Commandes"
          value={stats.totalOrders}
          icon={<ShoppingCart size={32} />}
          color="primary"
        />
        <StatCard
          title="Panier Moyen"
          value={formatCurrency(stats.averageOrderValue)}
          icon={<Package size={32} />}
          color="info"
        />
        <StatCard
          title="Ventes ce Mois"
          value={Object.keys(stats.salesByMonth).length}
          icon={<Calendar size={32} />}
          color="warning"
          subtitle="Mois actifs"
        />
      </div>

      <div className="sales-content">
        {/* Top Produits */}
        <div className="sales-section">
          <div className="sales-card">
            <h3>Top 5 des Produits Vendus</h3>
            {stats.topProducts.length > 0 ? (
              <div className="sales-table">
                <table>
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Quantité</th>
                      <th>CA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topProducts.map((product, index) => (
                      <tr key={index}>
                        <td>{product.name}</td>
                        <td>{product.quantity}</td>
                        <td>{formatCurrency(product.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Aucune donnée de vente disponible</p>
            )}
          </div>
        </div>

        {/* Ventes par Mois */}
        <div className="sales-section">
          <div className="sales-card">
            <h3>Ventes par Mois</h3>
            {Object.keys(stats.salesByMonth).length > 0 ? (
              <div className="sales-table">
                <table>
                  <thead>
                    <tr>
                      <th>Mois</th>
                      <th>Commandes</th>
                      <th>CA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(stats.salesByMonth)
                      .sort(([a], [b]) => b.localeCompare(a))
                      .map(([month, data]) => (
                        <tr key={month}>
                          <td>
                            {new Date(month + '-01').toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long'
                            })}
                          </td>
                          <td>{data.count}</td>
                          <td>{formatCurrency(data.revenue)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Aucune donnée de vente disponible</p>
            )}
          </div>
        </div>

        {/* Dernières Ventes */}
        <div className="sales-section full-width">
          <div className="sales-card">
            <h3>Dernières Ventes</h3>
            {sales.length > 0 ? (
              <div className="sales-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Client</th>
                      <th>Produits</th>
                      <th>Total</th>
                      <th>Paiement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.slice(-10).reverse().map((sale) => (
                      <tr key={sale.id}>
                        <td>{formatDate(sale.date)}</td>
                        <td>{sale.customer}</td>
                        <td>{sale.items.length} produit(s)</td>
                        <td>{formatCurrency(sale.total)}</td>
                        <td>
                          <span className="payment-chip">
                            {sale.paymentMethod}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Aucune vente enregistrée</p>
            )}
          </div>
        </div>
      </div>

      {/* Dialog de confirmation de réinitialisation */}
      {showResetDialog && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h3>Confirmer la réinitialisation</h3>
            <p>Êtes-vous sûr de vouloir réinitialiser toutes les données de vente ? Cette action est irréversible.</p>
            <div className="dialog-actions">
              <button onClick={() => setShowResetDialog(false)}>
                Annuler
              </button>
              <button onClick={handleResetSales} className="danger-btn">
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesDashboard;
